import { FC } from "react";
import { useTranslation } from "react-i18next";

import { useFormats } from "@ryde/shared";

import { IPlaybackModel } from "../investigation.models";
import {
  DelayChipStyled,
  PanelSectionStyled,
  StationRowStyled,
} from "../investigation.panel.styled";
import {
  delayColor,
  dwellLabel,
  formatSignedMinutes,
} from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";
import { usePlaybackSnapshot } from "../playback/use-playback-snapshot";

/** Ordered station list with scheduled-vs-actual times, dwell, delay and passenger flow; click seeks. */
export const InvestigationStationList: FC<{ model: IPlaybackModel }> = ({
  model,
}) => {
  const { t } = useTranslation();
  const { formatTimeNormalized } = useFormats();
  const { seekToMs } = usePlaybackContext();
  const snapshot = usePlaybackSnapshot();
  const currentIndex = snapshot?.currentStationIndex ?? -1;
  const label = (key: string): string =>
    t(`rideMap.investigation.stations.${key}`);

  return (
    <PanelSectionStyled>
      {model.stations.map((entry, index) => {
        const station = entry.station;

        return (
          <StationRowStyled
            key={station.id}
            $active={index === currentIndex}
            onClick={() => entry.tMs !== undefined && seekToMs(entry.tMs)}
          >
            <div className="station-index">{index + 1}</div>
            <div className="station-body">
              <div className="station-name">{station.name}</div>
              <div className="station-times">
                <span>
                  {label("scheduled")}:{" "}
                  {formatTimeNormalized(station.scheduledArrival)}
                </span>
                <span className={station.actualArrival ? undefined : "muted"}>
                  {label("actual")}:{" "}
                  {station.actualArrival
                    ? formatTimeNormalized(station.actualArrival)
                    : label("noArrival")}
                </span>
                {station.dwellSeconds ? (
                  <span>
                    {label("dwell")}: {dwellLabel(station.dwellSeconds)}
                  </span>
                ) : null}
                {station.delayMinutes !== undefined && (
                  <DelayChipStyled $color={delayColor(station.delayMinutes)}>
                    {formatSignedMinutes(station.delayMinutes)}
                  </DelayChipStyled>
                )}
              </div>
              <div className="station-flow">
                <span>↑ {station.boardingCount}</span>
                <span>↓ {station.alightingCount}</span>
                <span>
                  {label("occupancy")}: {station.occupancyAfter}
                </span>
              </div>
            </div>
          </StationRowStyled>
        );
      })}
    </PanelSectionStyled>
  );
};
